/**
 * Mastery Query Helpers — pure computation functions.
 *
 * These compute derived data from learner state. They are NOT actions —
 * they don't modify state, only read it.
 *
 * The learner ACTIONS (recordAnswer, markCheatSheetViewed, etc.) have been
 * moved to the Zustand store (app/store.ts) as part of the Account System
 * Refactor — Fase 1.
 *
 * Original location: src/hooks/learnerActions.ts
 * Moved to accounts/hooks/ as part of DDD Fase 2 (R20).
 * Learner actions removed and moved to Zustand store (s79).
 */

import { type ModuleId } from '../../content';
import { AppState } from '../../app/types';
import { GroupId, getStartingLevelForPeriod } from '../../config/systemConfig';
import { getAnsweredQuestionsForGroup, getRecentQuestionsForGroup, getReviewNeededCountForGroup, getAutoStartingTier, clampTierToModuleContent, getModuleMasteryFromFamilies } from '../../mastery';

/**
 * Mastery & placement query helpers.
 * These are pure functions that compute derived data from state.
 */
export function createMasteryQueries(state: AppState, selectedGroup: GroupId, manualPeriod: import('../../config/systemConfig').StartingPeriod | null) {
  const getStartingLevel = (moduleId: ModuleId, group: GroupId): number | undefined => {
    // Date-based (auto) or manual B/M/E starting tier. Both are content-blind:
    // they point at the period's tier for the group regardless of whether THIS
    // module actually has content there. We therefore snap the result to the
    // nearest tier with content for the module so a fresh learner never lands
    // on an empty band (e.g. GELD groep 6 at E6/E6+ — tiers 5–6).
    const baseTier = manualPeriod ? getStartingLevelForPeriod(group, manualPeriod) : getAutoStartingTier(group);
    return clampTierToModuleContent(baseTier, group, moduleId);
  };
  const getModuleMasteryForGroup = (moduleId: ModuleId): number => {
    return getModuleMasteryFromFamilies(state.familyMastery || {}, moduleId, selectedGroup);
  };
  const getAnsweredQuestions = (moduleId: ModuleId): string[] => {
    return getAnsweredQuestionsForGroup(moduleId, state.questionStates, selectedGroup);
  };
  const getRecentQuestions = (moduleId: ModuleId, limit?: number): string[] => {
    return getRecentQuestionsForGroup(moduleId, state.questionStates, selectedGroup, limit);
  };
  const getReviewNeededCount = (moduleId: ModuleId): number => {
    return getReviewNeededCountForGroup(moduleId, state.questionStates, selectedGroup);
  };
  return {
    getStartingLevel,
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    getRecentQuestions,
    getReviewNeededCount
  };
}